import * as React from 'react'
import { HTMLAttributeAnchorTarget, ReactNode } from 'react'
import clsx from 'clsx'
import Link from 'next/link'
import {
  ButtonAppearance,
  ButtonAppearances,
  ButtonColor,
} from '@/shared/components/buttons/ButtonConstants'

export type ButtonProps = {
  color: ButtonColor
  href?: string
  className?: string
  appearance?: ButtonAppearance
  loading?: boolean
  onClick?: (e: React.MouseEvent) => void
  disabled?: boolean
  type?: 'button' | 'submit'
  borderRadius?: 'sm' | 'md' | 'lg' | 'full'
  children: ReactNode
  target?: HTMLAttributeAnchorTarget
}

const linkAppearances = {
  primary: {
    red: 'text-white disabled:bg-red-400 bg-red-600 hover:bg-red-500',
    amber: 'text-white disabled:bg-amber-400 bg-amber-600 hover:bg-amber-500',
    blue: 'text-white disabled:bg-blue-400 bg-blue-600 hover:bg-blue-500',
    gray: 'text-white disabled:bg-gray-400 bg-gray-600 hover:bg-gray-500',
    slate: 'text-white disabled:bg-slate-400 bg-slate-600 hover:bg-slate-500',
    white:
      'text-slate-800 disabled:text-slate-300 bg-gray-50 hover:bg-gray-200',
  },
  secondary: {
    red: 'disabled:bg-red-50 disabled:text-red-300 bg-red-50 hover:bg-red-100 text-red-600',
    amber:
      'disabled:bg-amber-50 disabled:text-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-600',
    blue: 'disabled:bg-blue-50 disabled:text-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-600',
    gray: 'disabled:bg-gray-100 disabled:text-gray-400 bg-gray-300 hover:bg-gray-400 text-gray-700',
    slate:
      'disabled:bg-slate-100 disabled:text-slate-400 bg-slate-300 hover:bg-slate-400 text-slate-700',
    white: '',
  },
  ring: {
    red: 'ring-1 bg-white disabled:text-red-400 disabled:ring-red-300 ring-red-700 text-red-600 hover:bg-red-100',
    amber:
      'ring-1 bg-white disabled:text-amber-400 disabled:ring-amber-300 ring-amber-600 text-amber-600 hover:bg-amber-100',
    blue: 'ring-1 bg-white disabled:text-blue-400 disabled:ring-blue-300 ring-blue-600 text-blue-600 hover:bg-blue-100',
    gray: 'ring-1 bg-white disabled:text-gray-400 disabled:ring-gray-300 ring-gray-600 text-gray-600 hover:bg-gray-100',
    slate:
      'ring-1 bg-white disabled:text-slate-400 disabled:ring-slate-300 ring-slate-600 text-slate-600 hover:bg-slate-100',
    white: '',
  },
  text: {
    red: 'bg-white disabled:text-red-400 disabled:ring-red-300 enabled:ring-red-600 enabled:text-red-600',
    amber:
      'bg-white disabled:text-amber-400 disabled:ring-amber-300 enabled:ring-amber-600 enabled:text-amber-600',
    blue: 'bg-white disabled:text-blue-400 disabled:ring-blue-300 enabled:ring-blue-600 text-blue-600',
    gray: 'bg-white disabled:text-gray-400 disabled:ring-gray-300 enabled:ring-gray-600 enabled:text-gray-600',
    slate:
      'bg-white disabled:text-slate-400 disabled:ring-slate-300 enabled:ring-slate-600 enabled:text-slate-600',
    white: '',
  },
}

export default function Button({
  color,
  onClick,
  href,
  className = '',
  appearance = 'primary',
  loading = false,
  disabled = false,
  type = 'button',
  borderRadius = 'md',
  children,
  target,
}: ButtonProps) {
  const shouldDisable = disabled || loading
  let border

  switch (borderRadius) {
    case 'sm':
      border = 'rounded-sm'
      break
    case 'md':
      border = 'rounded-md'
      break
    case 'lg':
      border = 'rounded-lg'
      break
    case 'full':
      border = 'rounded-full'
      break
  }

  return (
    <>
      {href ? (
        <Link
          className={clsx(
            border,
            'px-2.5 py-1.5 font-sans text-sm font-semibold transition-colors',
            linkAppearances[appearance][color],
            shouldDisable ? 'cursor-not-allowed' : 'cursor-pointer',
            className,
            appearance !== 'text' && 'shadow=sm',
          )}
          href={href}
          {...(target && { target })}
        >
          {children}
        </Link>
      ) : (
        <button
          className={clsx(
            border,
            'relative px-2.5 py-1.5 font-sans text-sm font-semibold text-white transition-colors',
            ButtonAppearances[appearance][color],
            shouldDisable ? 'cursor-not-allowed' : 'cursor-pointer',
            className,
            appearance !== 'text' && 'shadow=sm',
          )}
          type={type}
          disabled={shouldDisable}
          onClick={onClick}
        >
          {children}
        </button>
      )}
    </>
  )
}
